/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box, Stack, Typography, TypographyProps } from "@mui/material";
import { useUnitsFormatter } from "hooks/formatters";
import { TFunction } from "i18next";
import React from "react";

export const CategoryTitle = ({
  children,
  ...props
}: React.PropsWithChildren<TypographyProps>) => {
  return (
    <Typography
      variant="button"
      fontWeight="medium"
      sx={{ textDecoration: "underline" }}
      {...props}
    >
      {children}
    </Typography>
  );
};

export const AddedItem = ({ children }: React.PropsWithChildren) => {
  return <CategoryTitle color="success.main">+ {children}</CategoryTitle>;
};

export const RemovedItem = ({ children }: React.PropsWithChildren) => {
  return <CategoryTitle color="error.main">- {children}</CategoryTitle>;
};

export const DiffValue = ({
  children,
  color,
}: React.PropsWithChildren<{
  color?: TypographyProps["color"];
}>) => {
  return (
    <Typography
      color={color}
      variant="body2"
      fontWeight="bold"
      whiteSpace="nowrap"
    >
      {children}
    </Typography>
  );
};

/**
 * One specific property diff row, to be used within a group
 */
export const DiffRow = ({
  title,
  value,
}: {
  title: string;
  value: React.ReactNode;
}) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      sx={{ pl: 2 }}
      gap={2}
    >
      <Typography variant="body2" flexShrink={0}>
        {title}
      </Typography>
      {value}
    </Box>
  );
};

export const DiffGroupWrapper = ({ children }: React.PropsWithChildren) => {
  return (
    <Box display="flex" flexDirection="column" alignItems="stretch">
      {children}
    </Box>
  );
};

interface NumericDiffPropsBase {
  format?: (diff: number) => string;
  prefix?: string;
  suffix?: string;
}

type NumericDiffProps = (
  | {
      diff: number;
      previous?: number;
      updated?: number;
    }
  | {
      diff?: never;
      previous: number;
      updated: number;
    }
) &
  NumericDiffPropsBase;
/**
 * If `diff` is not provided, `previous` and `updated` are used to calculate the diff (and thus required).
 * If `diff` is provided, `previous` and `updated` can still be provided to show the actual values.
 */
export const NumericDiff = ({
  diff,
  previous,
  updated,
  format,
  prefix,
  suffix,
}: NumericDiffProps) => {
  const calculatedDiff = diff ?? updated - previous;
  const prefixTrimmed = prefix?.trimStart() ?? "";
  const suffixTrimmed = suffix?.trimEnd() ?? "";
  return (
    <DiffValue color={calculatedDiff > 0 ? "success.main" : "error.main"}>
      {calculatedDiff > 0 ? "+ " : "- "}
      {prefixTrimmed}
      {format ? format(Math.abs(calculatedDiff)) : Math.abs(calculatedDiff)}
      {suffixTrimmed}
      {previous !== undefined && updated !== undefined
        ? ` (${prefixTrimmed}${
            format ? format(previous) : previous
          }${suffixTrimmed} → ${prefixTrimmed}${
            format ? format(updated) : updated
          }${suffixTrimmed})`
        : updated
          ? `(${prefixTrimmed}${format ? format(updated) : updated}${
              suffix?.toString() ?? ""
            })`
          : ""}
    </DiffValue>
  );
};

interface DiffBase<T> {
  key: string;
  previous?: T | null;
  updated?: T | null;
}

export interface AttributeDisplayConfig<T> {
  title: string;
  renderDiff: React.FC<{
    // This is only called after checking that both values exist
    updatedVal: T;
    previousVal: T;
  }>;
  // Null is not rendered
  format: (value: NonNullable<T>) => string | React.ReactNode;
}

export type CategoryConfig<TItem extends Record<string, unknown>> = {
  [Key in keyof TItem]?: AttributeDisplayConfig<TItem[Key]>;
};

export const DiffCategory = <TItem extends Record<string, unknown>>({
  diff,
  config,
  hideKeys,
}: {
  diff: DiffBase<TItem>;
  config: CategoryConfig<TItem>;
  hideKeys?: boolean;
}) => {
  const previous = diff.previous;
  const updated = diff.updated;
  const itemKey = diff.key;

  if (!updated) {
    if (hideKeys) return null;
    return (
      <DiffGroupWrapper>
        <RemovedItem>{itemKey}</RemovedItem>
      </DiffGroupWrapper>
    );
  } else if (!previous) {
    return (
      <DiffGroupWrapper>
        {!hideKeys && <AddedItem>{itemKey}</AddedItem>}
        {Object.entries(config).map(
          ([attrKey, config]: [
            keyof TItem,
            AttributeDisplayConfig<TItem[keyof TItem]>,
          ]) => {
            if (updated[attrKey] === null) return null;
            return (
              <DiffRow
                key={String(attrKey)}
                title={config.title}
                value={
                  <DiffValue>{config.format(updated[attrKey]!)}</DiffValue>
                }
              />
            );
          }
        )}
      </DiffGroupWrapper>
    );
  }

  return (
    <DiffGroupWrapper>
      {!hideKeys && <CategoryTitle>{itemKey}</CategoryTitle>}
      {Object.entries(config).map(
        ([attrKey, config]: [
          keyof TItem,
          AttributeDisplayConfig<TItem[keyof TItem]>,
        ]) => {
          if (updated[attrKey] === previous[attrKey]) return null;
          const Comp = config.renderDiff;
          const diffDisplay = (
            <Comp
              updatedVal={updated[attrKey]}
              previousVal={previous[attrKey]}
            />
          );
          if (diffDisplay === null) return null;

          return (
            <DiffRow
              key={String(attrKey)}
              title={config.title}
              value={diffDisplay}
            />
          );
        }
      )}
    </DiffGroupWrapper>
  );
};

interface Utils {
  t: TFunction;
  units: ReturnType<typeof useUnitsFormatter>;
  formatNumber2DP: (val: number) => string;
  formatNumber2DPFixed: (val: number) => string;
  isAdmin: boolean;
}
export type CategoryConfigFactory<TItem extends Record<string, unknown>> = (
  utils: Utils
) => CategoryConfig<TItem>;

export const PanelSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <Stack flexShrink={0}>
      <Typography variant="caption" fontWeight="bold">
        {title}
      </Typography>
      <Stack
        sx={{
          "&>*": {
            borderBottom: 1,
            borderColor: "divider",
            "&:last-child": {
              borderBottomWidth: 0,
              pb: 1,
            },
          },
        }}
      >
        {children}
      </Stack>
    </Stack>
  );
};

export const massConfig = ({
  formatNumber2DP,
  units,
}: Utils): Omit<
  AttributeDisplayConfig<number | null | undefined>,
  "title"
> => ({
  renderDiff: ({ updatedVal, previousVal }) => {
    return (
      <NumericDiff
        previous={previousVal ?? 0}
        updated={updatedVal ?? 0}
        suffix={units("mass")}
        format={formatNumber2DP}
      />
    );
  },
  format: (val) => `${formatNumber2DP(val)}${units("mass")}`,
});
